// © Microsoft Corporation. All rights reserved.

import { canUseDOM, getCookieString } from "@msnews/isomorphic-util";

import { SameSiteValues } from "@msnews/experiences-constants";

/**
 * Returns the cookie content given a name
 *
 * @param name {string} - cookie name
 * @returns the value of the cookie or `null`, if the key is not found.
 */
export function getCookie(name: string): string {
    const cookieString = getCookieString();

    if (cookieString) {
        const re = new RegExp("\\b" + name + "\\s*=\\s*([^;]*)", "i");
        const match = re.exec(cookieString);
        return (match && match.length > 1 ? match[1] : null);
    }

    return null;
}

/**
 * Helper function that retrieves the MUID from the document.cookie
 * or uses the saved value if the cookie was retrieved for the current page instance
 */
export function getMuidCookie(): string {
    return getCookie("MUID");
}

/**
 * Helper function used to set a cookie
 * NOTE: THIS BYPASSES ANY GDPR CHECK AND HENCE ONLY REQUIRED FUNCTIONAL COOKIES SHOULD BE SET USING THIS
 * IF YOU DO NOT KNOW IF YOUR COOKIE IS ONE THEN PLEASE USE Cookies.ts setCookie THAT MAKES USE OF GDPR LOGIC CHECKS
 * When expiryDays is equal to -1 it will unset the cookie
 * * @param {string} name Cookie name
 * * @param {string} value Cookie value
 * * @param {number} expiryDays Cookie expiry days
 * * @param {string} domain Cookie domain
 * * @param {string} path Cookie path
 * * @param {boolean} secure Is it a https only cookie
 * * @param {SameSiteValues} sameSiteValue What value should we use for samesite? also sets secure.
 */
export function setRequireFunctionalCookie(name: string, value: string, expiryDays?: number, domain?: string, path?: string, secure?: boolean, sameSiteValue?: SameSiteValues): void {
    let expiryDate;
    const builder = [name, "=", value];
    if (-1 === expiryDays) {
        // Expires date format is supposed to be in GMT.
        expiryDate = "Fri, 31 Dec 1999 23:59:59 GMT";
    } else if (expiryDays) {
        const date = new Date();
        date.setTime(date.getTime() + (expiryDays * 86400000)); // 86400000 == 24*60*60*1000 (ms/day)
        expiryDate = date.toUTCString();
    }

    if (expiryDate) { builder.push(";expires=", expiryDate); }
    if (domain) { builder.push(";domain=", domain); }
    if (path) { builder.push(";path=", path); }
    if (secure) { builder.push(";secure"); }
    // Do we have a value to set for samesite?  NOTE: the browser default is "lax" so we won't set when we have that value.
    if (sameSiteValue && sameSiteValue !== SameSiteValues.Lax) {
        builder.push(";samesite=", sameSiteValue.toString());
        // If samesite is "none" and we have not already set "secure".
        if (sameSiteValue === SameSiteValues.None && !secure) {
            builder.push(";secure");
        }
    }

    if (canUseDOM() && document) {
        document.cookie = builder.join("");
    }
}